import React, { useEffect, useRef } from 'react';
import Popup from '../Popup';
import styles from './PopupGraph.module.scss';
import { TLatexExpression } from '../../../typings/common';
import { logError } from '../../../utilities/helpers/errors';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  latexExpressions: TLatexExpression[];
  setLatexExpressions: React.Dispatch<React.SetStateAction<TLatexExpression[]>>;
};

const POPUP_ID = 'graph';

const isDesmosAvailable = (): boolean => {
  if (
    window.Desmos === undefined ||
    typeof window.Desmos.GraphingCalculator === 'undefined'
  ) {
    return false;
  }
  return true;
};

const PopupGraph: React.FC<Props> = (props) => {
  const { isOpen, onClose, latexExpressions, setLatexExpressions } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const calcRef = useRef<Desmos.Calculator | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!isDesmosAvailable()) {
      logError(new Error('Desmos API is not available'), {
        message: 'Demos API is not available. Please check public key.',
      });

      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    if (!calcRef.current && window.Desmos) {
      calcRef.current = window.Desmos.GraphingCalculator(container, {
        zoomButtons: true,
        settingsMenu: false,
        projectorMode: true,
      });
    }

    const calc = calcRef.current;
    if (!calc) {
      return;
    }

    calc.setBlank();
    calc.setExpressions(
      latexExpressions.map((expr) => ({ id: expr.id, latex: expr.latex }))
    );

    // eslint-disable-next-line consistent-return
    return () => {
      if (calcRef.current) {
        calcRef.current.destroy();
        calcRef.current = null;
      }
    };
  }, [isOpen, latexExpressions]);

  const handleClosePopup = () => {
    const calc = calcRef.current;

    if (calc) {
      const expressions: TLatexExpression[] = [];

      calc.getExpressions().forEach((expr) => {
        if (
          expr != null &&
          expr.type !== 'text' &&
          'latex' in expr &&
          typeof expr.id === 'string' &&
          typeof expr.latex === 'string' &&
          expr.latex.trim() !== ''
        ) {
          expressions.push({
            id: expr.id,
            latex: expr.latex,
          });
        }
      });

      setLatexExpressions(expressions);
    }

    onClose();
  };

  if (!isDesmosAvailable()) {
    return null;
  }

  return (
    <Popup
      isOpen={isOpen}
      onClose={handleClosePopup}
      id={POPUP_ID}
      size='lg'
      dialogClassName={styles.popupGraph}>
      <div
        ref={containerRef}
        className={styles.graphWrapper}
      />
    </Popup>
  );
};

export default PopupGraph;
